// ============================================================================
// KLINISOL CRO - Training Portal Page (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
    Search, 
    PowerOff, 
    CheckCircle, 
    Clock, 
    BarChart3, 
    FileText,
    Shield,
    ClipboardCheck,
    Award,
    Database,
    Stethoscope,
    FolderOpen,
    BookOpen,
    Play,
    RotateCcw,
    Loader2,
    AlertCircle,
    X,
    ArrowLeft,
    HelpCircle
} from 'lucide-react';

const TrainingPortal = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // State management
    const [modules, setModules] = useState([]);
    const [filteredModules, setFilteredModules] = useState([]);
    const [trainingRecords, setTrainingRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showCompleted, setShowCompleted] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    // Fetch data on component mount
    useEffect(() => {
        fetchTrainingModules();
        fetchTrainingRecords();
    }, []);

    // Apply filters when search term, category, or show completed changes
    useEffect(() => {
        applyFilters();
    }, [searchTerm, selectedCategory, showCompleted, modules, trainingRecords]);

    const fetchTrainingModules = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await api.get('/training');
            
            if (response.data.success) {
                setModules(response.data.data);
                setFilteredModules(response.data.data);
            } else {
                setError('Failed to load training modules');
            }
        } catch (err) {
            console.error('Error fetching training modules:', err);
            setError(err.response?.data?.message || 'Failed to load training modules. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchTrainingRecords = async () => {
        try {
            const response = await api.get('/training/records/my');
            if (response.data.success) {
                setTrainingRecords(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching training records:', err);
        }
    };

    const applyFilters = () => {
        let filtered = [...modules];

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(module => module.category === selectedCategory);
        }

        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(module =>
                module.module_name.toLowerCase().includes(search) ||
                module.module_code.toLowerCase().includes(search) ||
                (module.description && module.description.toLowerCase().includes(search))
            );
        }

        if (showCompleted) {
            const completedModuleIds = trainingRecords
                .filter(record => record.status === 'completed' && record.completion_date !== null)
                .map(record => record.module_id);
            filtered = filtered.filter(module => completedModuleIds.includes(module.id));
        }

        setFilteredModules(filtered);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isModuleCompleted = (moduleId) => {
        const record = trainingRecords.find(record => record.module_id === moduleId);
        return record && record.status === 'completed' && record.completion_date !== null;
    };

    const getModuleRecord = (moduleId) => {
        return trainingRecords.find(record => record.module_id === moduleId);
    };

    const handleStartTraining = (moduleId, moduleName) => {
        console.log('Start training:', moduleId);
        navigate(`/training/module/${moduleId}`);
    };

    const formatCompletionDate = (dateString) => {
        if (!dateString) return 'In Progress';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString();
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    const getCategoryIcon = (category) => {
        const iconClass = "w-5 h-5";
        const icons = {
            'GCP': <ClipboardCheck className={iconClass} />,
            'Safety': <Shield className={iconClass} />,
            'Regulatory': <CheckCircle className={iconClass} />,
            'Quality': <Award className={iconClass} />,
            'Data Management': <Database className={iconClass} />,
            'Clinical Operations': <Stethoscope className={iconClass} />,
            'Documentation': <FolderOpen className={iconClass} />,
            'default': <BookOpen className={iconClass} />
        };
        return icons[category] || icons.default;
    };

    const getCategoryColor = (category) => {
        const colors = {
            'GCP': 'bg-blue-500',
            'Safety': 'bg-red-500',
            'Regulatory': 'bg-cyan-500',
            'Quality': 'bg-indigo-500',
            'Data Management': 'bg-green-500',
            'Clinical Operations': 'bg-orange-500',
            'Documentation': 'bg-purple-500',
            'default': 'bg-gray-500'
        };
        return colors[category] || colors.default;
    };

    const categories = [...new Set(modules.map(m => m.category))];

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold">
                                KLIN<span className="text-[#E91E63]">i</span>SOL Training Portal
                            </h1>
                            <p className="text-sm opacity-90">Professional Development & Compliance Training</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right mr-3">
                                <p className="font-semibold text-sm">{user?.firstName} {user?.lastName}</p>
                                <p className="text-xs opacity-90">{user?.role}</p>
                            </div>
                            <button 
                                onClick={() => navigate('/training/records')}
                                className="px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg transition-all font-medium text-sm flex items-center gap-2"
                            >
                                <Award className="w-4 h-4" />
                                My Certificates
                            </button>
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg transition-all font-medium text-sm"
                            >
                                Dashboard
                            </button>
                            <button 
                                onClick={() => setShowHelp(true)}
                                className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-all flex items-center justify-center"
                                title="How Training Works"
                            >
                                <HelpCircle className="w-5 h-5" />
                            </button>
                            <button 
                                onClick={handleLogout}
                                className="w-11 h-11 rounded-full bg-gradient-to-br from-[#475569] to-[#E91E63] hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center group"
                                title="Logout"
                            >
                                <PowerOff className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Page Title */}
                <div className="mb-8">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-[#475569] mb-4 transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium">Back to Dashboard</span>
                    </button>
                    
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Training Modules</h2>
                    <p className="text-gray-600">Complete your required training and stay compliant</p>
                </div>

                {/* Filters Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4 mb-4">
                        {/* Search Box */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search training modules..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all"
                            />
                        </div>

                        {/* Category Filter */}
                        <div className="flex items-center gap-2">
                            <label htmlFor="category" className="font-medium text-gray-700 whitespace-nowrap">
                                Category:
                            </label>
                            <select
                                id="category"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all bg-white"
                            >
                                <option value="all">All Categories</option>
                                {categories.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {/* Completed Filter */}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showCompleted}
                                onChange={(e) => setShowCompleted(e.target.checked)}
                                className="w-5 h-5 text-[#475569] border-gray-300 rounded focus:ring-[#475569] focus:ring-2"
                            />
                            <span className="font-medium text-gray-700">Show only completed</span>
                        </label>
                    </div>

                    <div className="text-sm text-gray-600 font-medium">
                        Showing {filteredModules.length} of {modules.length} modules
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-12 h-12 text-[#475569] animate-spin mb-4" />
                        <p className="text-gray-600 text-lg">Loading training modules...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <p className="text-red-700 font-semibold mb-4">{error}</p>
                        <button 
                            onClick={fetchTrainingModules}
                            className="px-6 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Training Modules Grid */}
                {!loading && !error && filteredModules.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredModules.map((module) => {
                            const completed = isModuleCompleted(module.id);
                            const record = getModuleRecord(module.id);

                            return (
                                <div 
                                    key={module.id} 
                                    className={`bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-md ${
                                        completed 
                                            ? 'border-green-200 bg-green-50/30' 
                                            : 'border-gray-100 hover:border-[#E91E63]/30'
                                    }`}
                                >
                                    {/* Card Header */}
                                    <div className="p-4 border-b border-gray-100">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-sm font-semibold ${getCategoryColor(module.category)}`}>
                                                {getCategoryIcon(module.category)}
                                                <span>{module.category}</span>
                                            </div>
                                            {module.is_required && (
                                                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-lg text-xs font-semibold">
                                                    Required
                                                </span>
                                            )}
                                        </div>
                                        {completed && (
                                            <div className="flex items-center gap-2 text-green-600 font-semibold text-sm">
                                                <CheckCircle className="w-4 h-4" />
                                                <span>Completed</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                                            {module.module_name}
                                        </h3>
                                        <p className="text-sm text-gray-500 font-mono mb-3">
                                            {module.module_code}
                                        </p>
                                        {module.description && (
                                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                                {module.description}
                                            </p>
                                        )}

                                        {/* Meta Information */}
                                        <div className="flex flex-wrap gap-3 mb-4">
                                            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
                                                <Clock className="w-4 h-4" />
                                                <span>{module.duration_minutes} min</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
                                                <BarChart3 className="w-4 h-4" />
                                                <span>Pass: {module.passing_score}%</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
                                                <FileText className="w-4 h-4" />
                                                <span>v{module.version}</span>
                                            </div>
                                        </div>

                                        {/* Completion Info */}
                                        {completed && record && (
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm">
                                                <p className="text-gray-700 mb-1">
                                                    <span className="font-semibold">Completed:</span> {formatCompletionDate(record.completion_date)}
                                                </p>
                                                {record.score && (
                                                    <p className="text-gray-700">
                                                        <span className="font-semibold">Score:</span> {record.score}%
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Action Button */}
                                        <button 
                                            onClick={() => handleStartTraining(module.id, module.module_name)}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                                                completed
                                                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    : 'bg-gradient-to-r from-[#475569] to-[#334155] text-white hover:from-[#334155] hover:to-[#1e293b] shadow-lg'
                                            }`}
                                        >
                                            {completed ? (
                                                <>
                                                    <RotateCcw className="w-5 h-5" />
                                                    Retake
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="w-5 h-5" />
                                                    Start Training
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* No Results */}
                {!loading && !error && filteredModules.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                        <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">No Training Modules Found</h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm || selectedCategory !== 'all' || showCompleted
                                ? 'Try adjusting your filters or search term.'
                                : 'No training modules are currently available.'}
                        </p>
                        {(searchTerm || selectedCategory !== 'all' || showCompleted) && (
                            <button 
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('all');
                                    setShowCompleted(false);
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#475569] text-white font-semibold rounded-lg hover:bg-[#334155] transition-all"
                            >
                                <X className="w-5 h-5" />
                                Clear Filters
                            </button>
                        )}
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">
                        KLINiSOL
                    </h3>
                    <p className="text-sm opacity-90">&copy; 2025 KLINiSOL CRO. All rights reserved.</p>
                </div>
            </footer>

            {/* Help Modal */}
            {showHelp && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-[#475569] to-[#E91E63] text-white p-6 rounded-t-2xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <HelpCircle className="w-8 h-8" />
                                    <h2 className="text-2xl font-bold">Training Compliance Guide</h2>
                                </div>
                                <button 
                                    onClick={() => setShowHelp(false)}
                                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <p className="text-white/90 mt-2 text-sm">FDA 21 CFR Part 11 Compliant Training Platform</p>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Section 1: Timed Sections */}
                            <div className="border-l-4 border-[#475569] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-[#475569]" />
                                    Section Completion Requirements
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Each section has a <strong>minimum required reading time</strong> based on content length</li>
                                    <li>• The "Continue" button activates only after the timer completes</li>
                                    <li>• Time spent is tracked and recorded in your training record</li>
                                    <li>• You may spend additional time reviewing content as needed</li>
                                </ul>
                            </div>

                            {/* Section 2: Sequential Progression */}
                            <div className="border-l-4 border-[#E91E63] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-[#E91E63]" />
                                    Sequential Progression
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Sections must be completed <strong>in order</strong> - no skipping allowed</li>
                                    <li>• Previous sections are marked with a ✓ checkmark when complete</li>
                                    <li>• Locked sections display a 🔒 icon until prerequisites are met</li>
                                    <li>• This ensures comprehensive understanding before advancing</li>
                                </ul>
                            </div>

                            {/* Section 3: Knowledge Assessment */}
                            <div className="border-l-4 border-[#475569] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <ClipboardCheck className="w-5 h-5 text-[#475569]" />
                                    Knowledge Assessment
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Each module concludes with a <strong>quiz assessment</strong></li>
                                    <li>• Minimum passing score: <strong>80%</strong></li>
                                    <li>• Maximum <strong>3 attempts</strong> per assessment</li>
                                    <li>• All attempts are logged with score, date, and time</li>
                                    <li>• Contact your training administrator if additional attempts are needed</li>
                                </ul>
                            </div>

                            {/* Section 4: Electronic Signature */}
                            <div className="border-l-4 border-[#E91E63] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-[#E91E63]" />
                                    Electronic Signature & Certification
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Upon passing, you must provide an <strong>electronic signature</strong></li>
                                    <li>• Enter your <strong>full legal name</strong> exactly as registered</li>
                                    <li>• Enter your <strong>secure PIN</strong> for identity verification</li>
                                    <li>• This constitutes a legally binding signature per 21 CFR Part 11</li>
                                </ul>
                            </div>

                            {/* Section 5: Certificate Validity */}
                            <div className="border-l-4 border-[#475569] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <Award className="w-5 h-5 text-[#475569]" />
                                    Certificate Validity
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Certificates are valid for <strong>24 months</strong> from completion date</li>
                                    <li>• Each certificate includes a <strong>QR code</strong> for instant verification</li>
                                    <li>• Certificates are <strong>blockchain-secured</strong> for tamper-proof authenticity</li>
                                    <li>• Recertification required before expiration</li>
                                </ul>
                            </div>

                            {/* Section 6: Audit Trail */}
                            <div className="border-l-4 border-[#E91E63] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-[#E91E63]" />
                                    Audit Trail
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• All activity is automatically logged: start time, completion time, quiz attempts, signatures</li>
                                    <li>• Records are immutable and available for regulatory inspection</li>
                                    <li>• Training history accessible via "My Certificates" button</li>
                                </ul>
                            </div>

                            {/* Footer Note */}
                            <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-600 border border-gray-200">
                                <p>For questions, contact your training administrator.</p>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-gray-200 flex justify-end">
                            <button 
                                onClick={() => setShowHelp(false)}
                                className="px-6 py-2 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white font-semibold rounded-lg hover:opacity-90 transition-all"
                            >
                                Got It
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingPortal;